import { Link } from "react-router-dom"
import styled, { css } from "styled-components"
import { lighten, readableColor, darken } from "polished"
import breakpoint from "styled-components-breakpoint"

const getButtonColorFromVariant = (variant, theme, useAlternative = false) => {
  switch (variant) {
    case "simpleForm":
      return theme.colors.primary
    case "simpleFormDanger":
      return theme.colors.danger
    case "simpleFormBack":
      return theme.colors.fields.backButton
    case "primary":
      return theme.colors.primary
    case "secondary":
      if (!!useAlternative) {
        return theme.colors.secondaryAlternative || theme.colors.secondary
      } else {
        return theme.colors.secondary
      }
    case "white":
      if (!!useAlternative) {
        return theme.colors.whiteAlternative || "white"
      } else {
        return "white"
      }
    case "success":
      return theme.colors.success
    case "danger":
      return theme.colors.danger
    case "special":
      return theme.colors.buttons.special.main
    case "profilePrimary":
      return theme.colors.profile.buttons.primary.background
    case "profileSecondary":
      return theme.colors.profile.buttons.secondary.background
    default:
      return theme.colors.lightGrey
  }
}

const getTextColorFromVariant = (variant, theme, simpleForm = false) => {
  switch (variant) {
    case "simpleForm":
    case "simpleFormDanger":
      return theme.colors.buttons.lightColor
    case "simpleFormBack":
      return theme.colors.fields.backButtonText
    case "primary":
      return theme.colors.buttons.lightColor
    case "secondary":
      return theme.colors.secondaryAlternative || theme.colors.buttons.lightColor
    case "white":
      return theme.colors.buttons.darkColor
    case "success":
      // special case for CL button
      if (simpleForm && theme.colors.buttons.success) {
        return theme.colors.buttons.success.text
      } else {
        return readableColor(
          theme.colors.success,
          theme.colors.buttons.darkColor,
          theme.colors.buttons.lightColor,
          false
        )
      }
    case "danger":
      return theme.colors.buttons.lightColor
    case "special":
      return theme.colors.buttons.special.text
    case "profilePrimary":
      return theme.colors.profile.buttons.primary.text
    case "profileSecondary":
      return theme.colors.profile.buttons.secondary.text
    default:
      return theme.colors.buttons.darkColor
  }
}

const getFontSizeFromSize = (size, theme, simpleForm) => {
  if (simpleForm === true) return "16px"

  switch (size) {
    case "small":
      return "0.75rem"
    case "medium":
      return "1.25rem"
    case "large":
      return "1.5rem"
    default:
      return "1rem"
  }
}

const getPaddingFromSize = (size, theme, variant, iconOnly) => {
  if ((variant == "simpleForm") === true) return "12px 24px"

  switch (size) {
    case "small":
      return "0.5rem 1rem"
    case "medium":
      return "1rem 2rem"
    case "large":
      return "1.25rem 2.5rem"
    case "profileHeader":
      return !!iconOnly ? "12px 15px" : "10px 18px"
    default:
      return "0.75rem 1.5rem"
  }
}

export const styles = css`
  border: 1px solid ${({ variant, theme }) => getButtonColorFromVariant(variant, theme, true)};
  border-radius: 0.5rem;
  margin: 0;
  text-decoration: none;
  text-align: center;
  font-family: ${({ theme }) => theme.fonts.body.family}, sans-serif;
  font-weight: 500;
  appearance: none;
  cursor: pointer;
  white-space: nowrap;
  background-color: ${({ variant, theme }) => getButtonColorFromVariant(variant, theme)};
  color: ${({ variant, theme, simpleForm }) => getTextColorFromVariant(variant, theme, !!simpleForm)};
  font-size: ${({ size, theme, variant }) =>
    getFontSizeFromSize(size, theme, variant == "simpleForm" || variant == "simpleFormDanger")};
  padding: ${({ size, theme, variant, iconOnly }) => getPaddingFromSize(size, theme, variant, !!iconOnly)};
  box-sizing: border-box;

  &:disabled,
  &[disabled] {
    color: #aaa;
    background-color: #ddd;
    border-color: #ddd;
    pointer-events: none;
  }

  ${({ variant, outlined }) =>
    variant !== "simpleFormBack" &&
    !outlined &&
    css`
      &:not([disabled]):hover,
      &:not(:disabled):hover {
        background-color: ${({ variant, theme }) => lighten(0.05, getButtonColorFromVariant(variant, theme))};
        border-color: ${({ variant, theme }) => getButtonColorFromVariant(variant, theme, true)};
      }
    `}

  ${({ variant }) =>
    variant == "simpleFormBack" &&
    css`
      &:not([disabled]):hover,
      &:not(:disabled):hover {
        background-color: white;
        color: ${({ theme }) => theme.colors.brand};
        border-color: ${({ theme }) => theme.colors.brand};
      }
    `}

  ${(props) =>
    !!props.outlined &&
    css`
      background-color: transparent;

      &:disabled,
      &[disabled] {
        color: inherit;
        background-color: transparent;
        pointer-events: none;
        opacity: 0.5;
      }

      ${(props) =>
        props.variant !== "simpleFormBack" &&
        props.variant !== "profileSecondary" &&
        css`
          color: ${getButtonColorFromVariant(props.variant, props.theme, true)};

          &:not([disabled]):hover,
          &:not(:disabled):hover {
            background-color: ${getButtonColorFromVariant(props.variant, props.theme)};
            color: ${getTextColorFromVariant(props.variant, props.theme)};
          }
        `}

      ${(props) =>
        props.variant === "simpleFormBack" &&
        css`
          color: ${getTextColorFromVariant("simpleFormBack", props.theme)};
        `}

      ${(props) =>
        props.variant === "profileSecondary" &&
        css`
          &:not([disabled]):hover,
          &:not(:disabled):hover {
            background-color: ${darken(0.05, props.theme.colors.profile.headerBg)};
          }
        `}
    `}


  ${(props) =>
    !!props.fullwidth &&
    css`
      display: block;
      width: 100%;
    `}

  ${(props) =>
    !!props.fullHeight &&
    css`
      height: 100%;
    `}

  ${(props) =>
    !!props.inlineBlock &&
    css`
      display: inline-block;
    `}

  ${(props) =>
    !!props.fitContent &&
    css`
      width: fit-content;
    `}

  ${(props) =>
    !!props.number &&
    css`
      display: inline-flex;
      align-items: center;
    `}

  ${(props) =>
    !!props.profileMoreButtonsFooter &&
    css`
      border: none;
      color: #333;
      text-align: left;

      &:not([disabled]):hover,
      &:not(:disabled):hover {
        background-color: transparent;
        color: #434343;
      }
    `}

  ${(props) =>
    !!props.withIcon &&
    css`
      display: flex;
      align-items: center;
      justify-content: start;
      gap: 0.55rem;
    `}

  ${(props) =>
    !!props.tableCellLink &&
    css`
      background: transparent;
      padding: 0;
      text-decoration: underline;
      border: none;
      color: ${({ theme }) => theme.colors.primary};

      display: flex;
      align-items: center;
      justify-content: start;
      gap: 0.5rem;

      &:not([disabled]):hover,
      &:not(:disabled):hover {
        background: transparent;
        border: none;
        text-decoration: none;
      }
    `}
`

export const StyledLink = styled(Link)`
  ${styles}
`

export const StyledA = styled.a`
  ${styles}
`

export const StyledButton = styled.button`
  ${styles}

  ${(props) =>
    !!props.flexGrow &&
    css`
      flex-grow: 1;
    `}

  ${(props) =>
    (props.variant === "simpleForm" || props.variant === "simpleFormDanger") &&
    !!props.disabled &&
    !!props.actionButton &&
    css`
      opacity: 0.3;
      background-color: ${({ theme }) => theme.colors.primary} !important;
      border-color: transparent !important;
      color: ${({ theme }) => theme.colors.profile.buttons.secondary.text} !important;
    `}

  ${(props) =>
    (props.variant === "simpleForm" || props.variant === "simpleFormDanger") &&
    !!props.actionButton &&
    css`
      width: 100%;
      ${breakpoint("tablet")`
        width: initial;
      `}
    `}
`
export const Number = styled.button`
  margin-left: 0.75rem;
  text-decoration: none;
  text-align: center;
  font-family: ${({ theme }) => theme.fonts.body.family}, sans-serif;
  font-weight: bold;
  font-size: 0.8rem;
  white-space: nowrap;
  background-color: ${({ variant, theme }) => getTextColorFromVariant(variant, theme)};
  color: ${({ variant, theme }) => getButtonColorFromVariant(variant, theme)};
  border: none;
  border-radius: 0.75rem;
  min-height: 1.5rem;
  min-width: 1.5rem;
  margin-top: -0.5rem;
  margin-bottom: -0.5rem;
  padding: 4px 4px;

  ${(props) =>
    !!props.outlined &&
    css`
      background-color: ${getButtonColorFromVariant(props.variant, props.theme)};
      color: ${getTextColorFromVariant(props.variant, props.theme)};

      ${StyledButton}:not([disabled]):hover, ${StyledButton}:not(:disabled):hover & {
        background-color: ${getTextColorFromVariant(props.variant, props.theme)};
        color: ${getButtonColorFromVariant(props.variant, props.theme)};
      }
    `}
`
